import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { subjects, studies, subjectsWizard } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { SUBJECT_REFERENCE_ID_LABEL } from '@/lib/subjectLabel';

export function Step1Screening({
  subjectId,
  onComplete,
  triggerRef,
  onPendingChange,
}: {
  subjectId: string;
  onComplete: () => void;
  triggerRef?: React.MutableRefObject<(() => void) | null>;
  onPendingChange?: (pending: boolean) => void;
}) {
  const { user } = useAuth();

  const subjectQ = useQuery({
    queryKey: ['subject', subjectId],
    queryFn: () => subjects.detail(subjectId),
    enabled: !!subjectId,
    staleTime: 60_000,
  });

  const studiesQ = useQuery({
    queryKey: ['studies'],
    queryFn: () => studies.list(),
    staleTime: 300_000,
  });

  const subject = subjectQ.data?.subject;
  // The Subject ID: the subject's number from the IVRS/IWRS system. REQUIRED
  // to complete screening (client, 16 Oct 2026).
  const [ivrfNumber, setIvrfNumber] = useState('');
  const [ivrfError, setIvrfError] = useState<string | null>(null);
  const savedIvrfNumber = subjectQ.data?.wizard_state?.ivrf_number ?? '';
  useEffect(() => {
    if (savedIvrfNumber) setIvrfNumber(savedIvrfNumber);
  }, [savedIvrfNumber]);
  const study = studiesQ.data?.rows.find((s) => s.study_id === subject?.study_id);
  const siteName = subject?.site_id ? (user?.siteNames?.[subject.site_id] ?? subject.site_id) : '—';

  // Auto-record screening pass (subject was created, which implies pre-screening approval)
  const save = useMutation({
    mutationFn: () =>
      subjectsWizard.recordScreening(subjectId, {
        outcome: 'pass',
        ivrf_number: ivrfNumber.trim() || null,
      }),
    onSuccess: () => onComplete(),
    onError: (e) => toast.error(`Could not record screening: ${e.message}`),
  });

  useEffect(() => {
    if (triggerRef) {
      triggerRef.current = () => {
        if (!ivrfNumber.trim()) {
          setIvrfError('Subject ID is required.');
          toast.error('Enter the Subject ID (from the IVRS / IWRS) to continue.');
          return;
        }
        save.mutate();
      };
    }
  });

  useEffect(() => {
    onPendingChange?.(save.isPending);
  }, [save.isPending, onPendingChange]);

  const isLoading = subjectQ.isLoading || studiesQ.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-ink-muted">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <h2 className="text-base font-semibold text-ink-primary">Screening</h2>
      <p className="text-xs text-ink-muted">
        Review the subject details below. Click <strong>Next</strong> to proceed to informed consent.
      </p>

      <dl className="divide-y divide-surface-border rounded border border-surface-border text-sm">
        <div className="flex items-center px-4 py-3">
          <dt className="w-40 flex-shrink-0 text-xs font-medium text-ink-secondary">{SUBJECT_REFERENCE_ID_LABEL}</dt>
          <dd className="font-mono text-ink-primary">{subject?.external_ref ?? '—'}</dd>
        </div>
        <div className="flex items-center px-4 py-3">
          <dt className="w-40 flex-shrink-0 text-xs font-medium text-ink-secondary">Study</dt>
          <dd className="text-ink-primary">
            {study ? (
              <>
                <span className="font-medium">{study.title}</span>
                <span className="ml-2 text-xs text-ink-muted">({study.protocol_number})</span>
              </>
            ) : '—'}
          </dd>
        </div>
        <div className="flex items-center px-4 py-3">
          <dt className="w-40 flex-shrink-0 text-xs font-medium text-ink-secondary">Site</dt>
          <dd className="text-ink-primary">{siteName}</dd>
        </div>
      </dl>

      <label className="mt-4 block text-xs">
        <span className="font-medium text-ink-secondary">
          Subject ID <span className="text-red-600">*</span>
        </span>
        <input
          type="text"
          maxLength={60}
          required
          aria-invalid={!!ivrfError}
          placeholder="From the IVRS / IWRS"
          value={ivrfNumber}
          onChange={(e) => {
            setIvrfNumber(e.target.value);
            if (e.target.value.trim()) setIvrfError(null);
          }}
          className={`mt-1 w-full rounded border px-3 py-2 text-sm ${ivrfError ? 'border-red-500' : 'border-surface-border'}`}
        />
        {ivrfError && <div className="mt-1 text-[11px] text-red-600">{ivrfError}</div>}
        <div className="mt-1 text-[11px] text-ink-muted">
          The subject's number issued by the IVRS/IWRS system. Required to
          complete screening. Separate from the Subject Reference ID, which
          DreamTrials assigns automatically, and from the randomization number
          assigned later.
        </div>
      </label>
    </div>
  );
}
